import {_} from 'lodash';
import {moment} from 'moment';
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {
    IFormTemplate,
    IObjectData,
    IRecievedMessage,
    IRouteDefinitionParams,
    ITableColumn,
    TRouteId
} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {createErrorMessage, decodeAndStripHtml, getSearchMarkup} from "../../../util/misc";
import {global, lm} from "../../../appContext";
import {convertFromServerString} from "../../../util/datetime";
import {nsFa} from "../../fa/fn";
import {ISortableTableParams} from "../../sortable/index";
import ObjectDetails from "../../object/ObjectDetails";
import {nsMessages} from "../../messages/fn";
import {getHash, getRouteDefinition, setRouteHash} from "../../../constants/RouteDefinitions";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import {getSearchParamsFn} from "../../../util/request";
import {MAX_RESULTS_PER_PAGE} from "../../../constants";
import i = nsFa.i;
import MessageTypeId = nsMessages.MessageTypeId;
import MessageTypeIdToName = nsMessages.MessageTypeIdToName;
import detailsHtml from './details.html?raw';

export let template = detailsHtml;

type T = IRecievedMessage;

type TMessageType = 'ehub task' | 'ehub message' | 'sync message'

interface IMessageType {
    icon: string,
    id: MessageTypeId
    label: string
    type: TMessageType
}

export class viewModel extends ObjectDetails {
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly reqUpdateMessage: PromiseRequest = global.getPromiseRequest('updatereceivedmessage');
    private readonly searchkey: KnockoutObservable<string | null> = ko.observable(null);
    private readonly toggleNew: KnockoutObservable<boolean> = ko.observable(false);
    private readonly selectedType: KnockoutObservable<TMessageType | null> = ko.observable(null);
    private readonly req_searchformtempalte: PromiseRequest = new PromiseRequest('searchformtemplate')

    /** Heading badge mirrors the visible list (full set when idle, filtered when searching). */
    private readonly searchBadge = ko.pureComputed((): number | null => {
        if (!this.isReady()) {
            return null;
        }
        return this.dataProcessed().length;
    });

    /** Spinner on first load only (client-side filter is instant thereafter). */
    private readonly searchBadgeWaiting = ko.pureComputed((): boolean => !this.isReady());

    private TableColumns: ITableColumn<T>[] = [{
        key: 'id',
        label: 'id',
        sort: true,
    }, {
        key: 'subject',
        label: 'subject',
        sort: true,
    }, {
        key: 'messagetypeid',
        label: 'type',
        sort: true,
    }, {
        key: 'received',
        label: 'received',
        sort: true,
    }, {
        key: 'read',
        label: 'read|n:2',
        sort: true,
        showContent: o => !this.isSyncMessage(o),
    }, {
        key: 'completed',
        label: 'completed',
        sort: true,
        showContent: o => !this.isSyncMessage(o),
    }, {
        key: 'func',
        label: 'function',
        sort: false,
        noClickBubble: true,
        component: obj => {
            return {
                name: "component/user/messages/buttons",
                params: {
                    obj: obj,
                    onUpdate: this.onUpdateInstance.bind(this),
                    onError: this.onUpdateError.bind(this),
                },
            };
        },
        showContent: o => !this.isSyncMessage(o),
    }];

    private _data: KnockoutObservableArray<T> = ko.observableArray<T>([]);
    private allFormTemplates!: IFormTemplate[];

    constructor(params: IRouteDefinitionParams) {
        super(params, false);
        if (global.isLoggedInUser()) {
            this.kosub.subscribe(global.login.user, () => this.request());
        }
        this.request();
    }

    protected override request(): Promise<any> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});
        return Promise.resolve()
            .then(() => this.req_searchformtempalte
                .request(getSearchParamsFn('searchformtemplate')({resultsprpage: MAX_RESULTS_PER_PAGE}))
                .then((data: IFormTemplate[]) => this.allFormTemplates = data)
            )
            .then(() => this.__requestRouteData!
                .request()
                .then((data: any[]) => this.onSuccess(data as [IObjectData]))
            )
            .catch(this.onError.bind(this))
    }

    protected override onSuccess(data: [IObjectData]): Promise<[IObjectData]> {
        super.onSuccess(data);
        this._data(this.data()?.receivedmessages ?? []);
        this.messageData(null);
        this.isReady(true);
        return Promise.resolve(data);
    }

    private dataProcessed = ko.pureComputed((): T[] => {
        if (!this.isReady()) {
            return [];
        }
        let d = this._data();
        if (this.toggleNew()) {
            d = d.filter(o => o.istask ? !o.completed : !o.read);
        }
        if (this.selectedType()) {
            d = d.filter(o => this.isOfMessageType(o.messagetypeid, this.selectedType()!));
        }
        const searchkey = this.searchkey()?.toLowerCase() ?? '';
        if (searchkey) {
            d = d.filter((o: T) => {
                const keys = this.TableColumns.map(t => t.key) as (keyof T)[];
                return keys.find((k: keyof T): boolean => {
                    if (k in o) {
                        const val: any = (o as any)[k];
                        return this.getCellText(k, val, o as any).toLowerCase().indexOf(searchkey) != -1
                    }
                    return false;
                });
            })
        }
        return d;
    });

    private getCellContent(key: keyof T, val: T[keyof T], item: T): string {
        let str: string = this.getCellText(key, val, item);

        if (this.searchkey()) {
            str = getSearchMarkup(str, this.searchkey() ?? '')
        }

        switch (key) {
            case 'messagetypeid': {
                const icon = nsMessages.getCustomIcon(nsMessages.MessageTypeIdToName[item.messagetypeid])
                return i(icon) + ' ' + str
            }
            case 'message': {
                if (/^http(s)?:\/\//i.test(str)) {
                    str = `<a href="${val}">${lm.t('message')}</a>`;
                }
                break;
            }
        }

        return str;
    }

    private getFormTemplateById(id: IFormTemplate['id']): IFormTemplate | undefined {
        return this.allFormTemplates.find(o => o.id == id)
    }

    private getCellText(key: keyof T, val: T[keyof T], item: T): string {
        switch (key) {
            case 'messagetypeid': {
                return lm.t('be:' + MessageTypeIdToName[item.messagetypeid]) ?? ''
            }
            case 'subject': {
                return decodeAndStripHtml(item.messagetemplatename)
            }
            case 'completed':
            case 'read':
            case 'received': {
                if (val) {
                    return val ? convertFromServerString(String(val)).format('DD. MMMM YYYY HH:mm') : lm.t('No') ?? '';
                } else {
                    return lm.t('No') ?? ''
                }
            }
            default:
                return val ? String(val): '';
        }
    }

    private getCellSortValue(key: keyof T, val: T[keyof T], item: T): any {
        switch (key) {
            case 'completed':
            case 'read':
            case 'received':
                return val ? moment(val as string).unix() : 0;
            default:
                return this.getCellText(key, val, item);
        }
    }

    private onRowClick(obj: T) {
        if (
            this.isOfMessageType(obj.messagetypeid, 'ehub task') ||
            this.isOfMessageType(obj.messagetypeid, 'ehub message')
        ) {
            const taskUrl = obj.taskurl; // ex: https://mega.eadm.no/#/formview/NO940155223/102/799882
            const seg = new URL(taskUrl).hash.split('/')
            const formTemplateId = Number(seg[3]);
            if (!this.getFormTemplateById(formTemplateId)) {
                return false
            }
            if (!obj.read) {
                this.reqUpdateMessage.abort();
                this.reqUpdateMessage
                    .request({
                        id: obj.id,
                        read: true,
                        completed: !!obj.completed,
                    })
                    .then((data: any[]) => {
                        global.cacheManager.removeCache('getobject');
                        global.cacheManager.removeCache('searchobject');
                        return this.request();
                    })
            }
            const subject = decodeAndStripHtml(obj.subject);
            global.dialogManager.addConfirm({
                buttons: {
                    cancel: {
                        label: `${i('fa-times')} ((close))`,
                        cls: 'btn-default',
                    },
                    confirm: {
                        cls: 'btn-default',
                        onClick: () => {
                            this.openForm(obj)
                        },
                        label: 'open form for processing',
                    },
                },
                content: obj.message,
                fullWidth: false,
                title: `${i(obj.istask ? 'fa-clipboard' : 'fa-file-text')} ${subject}`,
            })
        } else {
            setRouteHash('messageflowdetails', {id: obj.messagetemplateid})
        }
    }

    private getTableParams(): ISortableTableParams<T> {
        return {
            TABLE: this.TableColumns,
            asc: ko.observable(false),
            data: this.dataProcessed,
            getCellContent: this.getCellContent.bind(this),
            getCellSortValue: this.getCellSortValue.bind(this),
            getCellText: this.getCellText.bind(this),
            onRowClick: (obj: T) => {
                this.onRowClick(obj);
                return false;
            },
            route: 'true',
            sortcol: ko.observable<keyof T>('received' as keyof T),
        }
    }

    private onUpdateInstance(obj: T) {
        this.request()
            .then((data: [IObjectData]) => {
                const obj = data[0];
                const user = global.login.user();
                if (this.__id == obj.id && user) {
                    user.receivedmessages = obj.receivedmessages;
                    global.login.user.valueHasMutated?.();
                }
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            });
    }

    private onUpdateError(reason: string) {
        this.messageData(createErrorMessage(String(reason)));
    }

    private isSyncMessage(obj: T): boolean {
        return this.getITypeObject(obj)?.type == 'sync message'
    }

    private getITypeObject(obj: T): IMessageType {
        const ret: IMessageType = {
            icon: '',
            id: obj.messagetypeid,
            label: '',
            type: 'sync message',
        }
        switch (obj.messagetypeid) {
            case MessageTypeId.eAdmUserTask:
            case MessageTypeId.eAdmManagerTask:
                return _.defaults({
                    icon: 'fa-clipboard',
                    label: '((eHub)) ((task|ac))',
                    type: 'ehub task',
                }, ret)
            case MessageTypeId.eAdmUserMessage:
            case MessageTypeId.eAdmManagerMessage:
                return _.defaults({
                    icon: 'fa-file-alt',
                    label: '((eHub)) ((message|ac))',
                    type: 'ehub message',
                }, ret)
            default:
                return _.defaults({
                    icon: getRouteDefinition('messageflowdetails')?.icon ?? '',
                    label: '((synchronization)) ((message|ac))',
                    type: 'sync message',
                }, ret)
        }
    }

    private isOfMessageType(messageTypeId: MessageTypeId, messageType: TMessageType): boolean {
        switch (messageType) {
            case "ehub message":
                return [
                    MessageTypeId.eAdmManagerMessage,
                    MessageTypeId.eAdmUserMessage,
                ].includes(messageTypeId)
            case "ehub task":
                return [
                    MessageTypeId.eAdmManagerTask,
                    MessageTypeId.eAdmUserTask,
                ].includes(messageTypeId)
            case "sync message":
                return ![
                    MessageTypeId.eAdmManagerMessage,
                    MessageTypeId.eAdmUserMessage,
                    MessageTypeId.eAdmManagerTask,
                    MessageTypeId.eAdmUserTask,
                ].includes(messageTypeId)
            default:
                return true
        }
    }

    private getCount(messageType: TMessageType): number {
        return this._data().filter(o => this.isOfMessageType(o.messagetypeid, messageType)).length
    }

    private openForm(message: T) {
        const url = new URL(document.location.origin + document.location.pathname)
        const taskUrl = message.taskurl; // ex: https://mega.eadm.no/#/formview/NO940155223/102/799882
        const seg = new URL(taskUrl).hash.split('/')
        const formTemplateId = Number(seg[3]);
        const formTemplate = this.getFormTemplateById(formTemplateId)
        if (!formTemplate) {
            return;
        }
        const routeId: TRouteId = formTemplate.v2form ? 'formview_v2' : 'formview'
        url.hash = getHash(routeId, {
            id: formTemplateId,
            orgnr: seg[2] ?? global.login.selectedOrganizationId() ?? '',
            preview: seg[5] ?? 0,
            targetid: seg[4] ?? 0,
        }) ?? ''
        window.open(url)
    }

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
        this.reqUpdateMessage.abort();
    }
}
